import { useEffect, useReducer } from 'react';

import { useCurrentProjectId } from '@adsk/alloy-provider';

const ACTIONS = {
  RESET: 'RESET',
  FETCH_INIT: 'FETCH_INIT',
  SET_CAN_CREATE: 'SET_CAN_CREATE',
  FETCH_FAILURE: 'FETCH_FAILURE',
};

const dataReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.RESET:
      return {
        canCreate: false,
        loading: !action.payload.disabled,
      };
    case ACTIONS.SET_CAN_CREATE:
      return {
        canCreate: action.payload,
        loading: false,
        error: false,
        retry: null,
      };
    case ACTIONS.FETCH_INIT:
      return {
        loading: true,
      };
    case ACTIONS.FETCH_FAILURE:
      return {
        canCreate: false,
        loading: false,
        error: true,
        retry: action.payload.retry,
      };
    default:
      throw new Error();
  }
};

/**
 * This hook returns plugin response for whether the current user can create a new entity or not.
 * Returned value:
 * canCreate - a boolean represents whether current user can create entity for given plugin.
 * loading - whether it currently loads data from BE.
 * error - whether fetching data raised an error.
 * retry - callback to trigger re-fetch upon error.
 *
 * @param plugin
 * @param disabled
 * @param additionalArgs
 * @returns {{loading: *, error: *, canCreate: *, retry: *}}
 */
export default (plugin, disabled, additionalArgs) => {
  const [{ error, loading, canCreate, retry }, dispatch] = useReducer(
    dataReducer,
    {
      canCreate: false,
      loading: !disabled,
    },
  );
  const projectId = useCurrentProjectId();

  useEffect(() => {
    dispatch({ type: ACTIONS.RESET, payload: { disabled } });
  }, [plugin, disabled]);

  useEffect(() => {
    let cancel;
    if (plugin && !disabled) {
      if (plugin.canCreate) {
        const fetchCanCreate = () =>
          plugin
            .canCreate({
              projectId,
              ...additionalArgs,
            })
            .then(
              (res) =>
                !cancel &&
                dispatch({ type: ACTIONS.SET_CAN_CREATE, payload: res }),
            )
            .catch((e) => {
              console.error(
                'ReferencePicker - useCanCreate: Error getting user permissions',
                e,
              );
              if (!cancel) {
                dispatch({
                  type: ACTIONS.FETCH_FAILURE,
                  payload: { retry: fetchCanCreate },
                });
              }
            });
        fetchCanCreate();
      } else {
        dispatch({ type: ACTIONS.SET_CAN_CREATE, payload: false });
      }
    }
    return () => {
      cancel = true;
    };
  }, [plugin, disabled, projectId, additionalArgs]);

  return {
    error,
    loading,
    canCreate,
    retry,
  };
};
